import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getProjects, createProject, deleteProject, Project } from '../services/api'
import { useWorkflow } from '../contexts/WorkflowContext'

// Gradient colors for folder icons
const gradientColors = [
  { border: 'rgba(59, 130, 246, 0.3)', bg: 'rgba(59, 130, 246, 0.15)', icon: '#60a5fa' },
  { border: 'rgba(139, 92, 246, 0.3)', bg: 'rgba(139, 92, 246, 0.15)', icon: '#a78bfa' },
  { border: 'rgba(34, 211, 238, 0.3)', bg: 'rgba(34, 211, 238, 0.15)', icon: '#22d3ee' },
  { border: 'rgba(244, 114, 182, 0.3)', bg: 'rgba(244, 114, 182, 0.15)', icon: '#f472b6' },
  { border: 'rgba(52, 211, 153, 0.3)', bg: 'rgba(52, 211, 153, 0.15)', icon: '#34d399' },
  { border: 'rgba(251, 146, 60, 0.3)', bg: 'rgba(251, 146, 60, 0.15)', icon: '#fb923c' },
]

function getGradientColor(index: number) {
  return gradientColors[index % gradientColors.length]
}

// Glass panel styles
const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

export default function Projects() {
  const queryClient = useQueryClient()
  const { workflowType, isPreSales } = useWorkflow()
  const [showModal, setShowModal] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('updated')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [formData, setFormData] = useState({
    customer_name: '',
    project_name: '',
    description: '',
  })

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects', workflowType],
    queryFn: async () => {
      const res = await getProjects()
      // Filter projects by workflow type
      // Treat null/undefined workflow_type as 'delivery' for backwards compatibility
      return res.data.filter((p) => {
        const projectWorkflow = p.workflow_type || 'delivery'
        return projectWorkflow === workflowType
      })
    },
  })

  const createMutation = useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setShowModal(false)
      setFormData({ customer_name: '', project_name: '', description: '' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate({
      ...formData,
      workflow_type: workflowType,
    })
  }

  const handleDelete = (e: React.MouseEvent, project: Project) => {
    e.preventDefault()
    e.stopPropagation()
    if (confirm(`Delete project "${project.project_name}"? This cannot be undone.`)) {
      deleteMutation.mutate(project.id)
    }
  }

  const projectList = Array.isArray(projects) ? projects : []

  if (projects && !Array.isArray(projects)) {
    console.error('Projects is not an array:', projects)
  }

  const stats = useMemo(() => {
    return {
      total: projectList.length,
      active: projectList.filter((p) => p.status === 'active').length,
      processing: projectList.filter((p) => p.status === 'processing').length,
      completed: projectList.filter((p) => p.status === 'completed').length,
    }
  }, [projectList])

  const customers = useMemo(() => {
    const unique = [...new Set(projectList.map((p) => p.customer_name))]
    return unique.sort()
  }, [projectList])

  const [customerFilter, setCustomerFilter] = useState('all')

  const filteredProjects = useMemo(() => {
    let filtered = projectList.filter(
      (p) =>
        p.customer_name.toLowerCase().includes(search.toLowerCase()) ||
        p.project_name.toLowerCase().includes(search.toLowerCase())
    )

    if (statusFilter !== 'all') {
      filtered = filtered.filter((p) => p.status === statusFilter)
    }

    if (customerFilter !== 'all') {
      filtered = filtered.filter((p) => p.customer_name === customerFilter)
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.project_name.localeCompare(b.project_name)
        case 'customer':
          return a.customer_name.localeCompare(b.customer_name)
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        default:
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      }
    })

    return filtered
  }, [projectList, search, statusFilter, customerFilter, sortBy])

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full"
            style={{ background: 'rgba(52, 211, 153, 0.15)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
            Active
          </span>
        )
      case 'processing':
        return (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full"
            style={{ background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
            Processing
          </span>
        )
      case 'completed':
        return (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full"
            style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
            Completed
          </span>
        )
      default:
        return (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full"
            style={{ background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8', border: '1px solid rgba(148, 163, 184, 0.3)' }}>
            New
          </span>
        )
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(59, 130, 246, 0.1)', border: '2px solid rgba(59, 130, 246, 0.3)', boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)' }}>
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading projects...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>
            {isPreSales ? 'Opportunities' : 'Projects'}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            {isPreSales ? 'Manage your pre-sales opportunities and discovery calls' : 'Manage your projects and engagements'}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder={isPreSales ? 'Search opportunities...' : 'Search projects...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-80 pl-10 pr-4 py-2.5 rounded-xl text-sm"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: isPreSales ? '1px solid rgba(52, 211, 153, 0.15)' : '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0'
              }}
            />
            <i className="fa-solid fa-search absolute left-3 top-3" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
            style={{
              background: isPreSales
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(20, 184, 166, 0.3) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
              border: isPreSales ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid rgba(96, 165, 250, 0.4)',
              color: '#e2e8f0'
            }}
          >
            <i className="fa-solid fa-plus"></i>
            <span>New Project</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={{ ...glassPanel, borderColor: 'rgba(59, 130, 246, 0.2)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              <i className="fa-solid fa-folder text-xl" style={{ color: '#60a5fa' }}></i>
            </div>
            <span className="text-xs font-medium px-3 py-1 rounded-full"
              style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              All
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Total Projects</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.total}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={{ ...glassPanel, borderColor: 'rgba(52, 211, 153, 0.2)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
              <i className="fa-solid fa-check-circle text-xl" style={{ color: '#34d399' }}></i>
            </div>
            <span className="text-xs font-medium px-3 py-1 rounded-full"
              style={{ background: 'rgba(52, 211, 153, 0.15)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
              Active
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Active Projects</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.active}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={{ ...glassPanel, borderColor: 'rgba(251, 191, 36, 0.2)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
              <i className="fa-solid fa-clock text-xl" style={{ color: '#fbbf24' }}></i>
            </div>
            <span className="text-xs font-medium px-3 py-1 rounded-full"
              style={{ background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
              Processing
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>In Progress</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.processing}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={{ ...glassPanel, borderColor: 'rgba(139, 92, 246, 0.2)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
              <i className="fa-solid fa-flag-checkered text-xl" style={{ color: '#a78bfa' }}></i>
            </div>
            <span className="text-xs font-medium px-3 py-1 rounded-full"
              style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
              Done
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Completed</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.completed}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Filter by:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
            </select>
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            >
              <option value="all">All Customers</option>
              {customers.map((customer) => (
                <option key={customer} value={customer}>{customer}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            >
              <option value="updated">Last Updated</option>
              <option value="name">Project Name</option>
              <option value="customer">Customer Name</option>
              <option value="created">Date Created</option>
            </select>
            <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid rgba(100, 180, 255, 0.15)' }}>
              <button
                onClick={() => setViewMode('grid')}
                className="px-3 py-2 transition"
                style={{ background: viewMode === 'grid' ? 'rgba(59, 130, 246, 0.2)' : 'transparent', color: viewMode === 'grid' ? '#60a5fa' : 'rgba(148, 163, 184, 0.6)' }}
              >
                <i className="fa-solid fa-th"></i>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className="px-3 py-2 transition"
                style={{ background: viewMode === 'list' ? 'rgba(59, 130, 246, 0.2)' : 'transparent', color: viewMode === 'list' ? '#60a5fa' : 'rgba(148, 163, 184, 0.6)' }}
              >
                <i className="fa-solid fa-list"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects && filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => {
            const colors = getGradientColor(index)
            return (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="rounded-xl transition-all duration-300 hover:scale-[1.02] group cursor-pointer"
                style={{ background: 'rgba(30, 41, 59, 0.5)', border: `1px solid ${colors.border}` }}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center"
                      style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
                      <i className="fa-solid fa-folder text-2xl" style={{ color: colors.icon }}></i>
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, project)}
                      className="opacity-0 group-hover:opacity-100 transition p-2 rounded-lg"
                      style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>
                  <h3 className="text-lg font-bold mb-1 transition" style={{ color: '#e2e8f0' }}>
                    {project.project_name}
                  </h3>
                  <p className="text-sm mb-3" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>{project.customer_name}</p>
                  {project.description && (
                    <p className="text-sm mb-4 line-clamp-2" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>{project.description}</p>
                  )}
                  <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}>
                    <div className="flex items-center space-x-4 text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                      <div className="flex items-center space-x-1">
                        <i className="fa-solid fa-microphone" style={{ color: '#a78bfa' }}></i>
                        <span>{project.recording_count}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <i className="fa-solid fa-file-alt" style={{ color: '#60a5fa' }}></i>
                        <span>{project.document_count}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">{getStatusBadge(project.status)}</div>
                </div>
              </Link>
            )
          })}
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
            <i className="fa-solid fa-folder-open text-3xl" style={{ color: 'rgba(96, 165, 250, 0.5)' }}></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            {search || statusFilter !== 'all' || customerFilter !== 'all'
              ? 'No projects match your filters'
              : 'No projects yet'}
          </h3>
          <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            {search || statusFilter !== 'all' || customerFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Get started by creating your first project'}
          </p>
          {!search && statusFilter === 'all' && customerFilter === 'all' && (
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 inline-flex items-center space-x-2 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
            >
              <i className="fa-solid fa-plus"></i>
              <span>Create your first project</span>
            </button>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <div className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
            <div className="sticky top-0 px-8 py-6 flex items-center justify-between"
              style={{ background: 'rgba(15, 23, 42, 0.95)', borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div>
                <h3 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Create New Project</h3>
                <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Start a new project engagement</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="transition"
                style={{ color: 'rgba(148, 163, 184, 0.6)' }}
              >
                <i className="fa-solid fa-times text-2xl"></i>
              </button>
            </div>

            <div className="px-8 py-6">
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Customer Name <span style={{ color: '#f87171' }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Acme Corporation"
                      required
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
                    />
                    <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                      Enter the name of the customer organization
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Project Name <span style={{ color: '#f87171' }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Q1 Assessment 2026"
                      required
                      value={formData.project_name}
                      onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
                    />
                    <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                      Provide a descriptive name for this engagement
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Description
                    </label>
                    <textarea
                      rows={4}
                      placeholder="Brief description of the engagement..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm resize-none"
                      style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
                    ></textarea>
                    <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                      Optional: Add details about the project scope and objectives
                    </p>
                  </div>

                  <div className="rounded-xl p-4" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                    <div className="flex space-x-3">
                      <i className="fa-solid fa-info-circle mt-0.5" style={{ color: '#60a5fa' }}></i>
                      <div>
                        <h4 className="text-sm font-semibold mb-1" style={{ color: '#60a5fa' }}>
                          What happens next?
                        </h4>
                        <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                          After creating the project, you can upload recordings, generate
                          transcripts, and create documentation.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-8 pt-6" style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-3 rounded-xl text-sm font-medium transition"
                    style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: 'rgba(148, 163, 184, 0.9)' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
                  >
                    {createMutation.isPending ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-plus"></i>
                        <span>Create Project</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
